import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Box, Stack, Skeleton, Grid } from '@mui/material';
import { keyframes } from '@mui/system';

// Advanced shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Pulse animation for subtle breathing effect
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
`;

// Gradient wave animation
const gradientWave = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const SkeletonCard = ({
  variant = 'stat', // 'stat', 'action', 'wide'
  animated = true,
  className,
  ...props
}) => {
  const getCardContent = () => {
    switch (variant) {
      case 'action':
        return (
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Skeleton
                variant="text"
                width="70%"
                height={32}
                sx={{
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                }}
              />
              <Stack direction="row" spacing={2} alignItems="center">
                <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.15)' }} />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={20}
                  sx={{
                    borderRadius: 1,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        );

      case 'wide':
        return (
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Skeleton variant="text" width="40%" height={40} sx={{ borderRadius: 2 }} />
                <Skeleton variant="circular" width={32} height={32} />
              </Stack>
              <Stack direction="row" spacing={3}>
                {[1, 2, 3, 4].map((item) => (
                  <Box key={item} sx={{ flex: 1 }}>
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 2, mb: 1 }} />
                  </Box>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        );

      case 'indicator-summary':
        return (
          <CardContent sx={{ p: 3, height: '100%' }}>
            <Stack spacing={3}>
              {/* Header section with title, caption, and icon */}
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="40%" height={16} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="60%" height={32} />
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="rectangular" width={80} height={28} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="circular" width={48} height={48} />
                </Stack>
              </Stack>

              {/* Indicator grid */}
              <Grid container spacing={1.5}>
                {[1, 2, 3, 4].map((item) => (
                  <Grid item xs={12} sm={6} key={item}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="circular" width={32} height={32} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Skeleton variant="text" width="50%" height={12} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="70%" height={24} />
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </CardContent>
        );

      default: // 'stat'
        return (
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="text" width="80%" height={24} sx={{ borderRadius: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="text" width="30%" height={32} sx={{ borderRadius: 1 }} />
              </Stack>
            </Stack>
          </CardContent>
        );
    }
  };

  return (
    <Card
      className={className}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        background:
          variant === 'action'
            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
            : 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        ...(animated && {
          animation: `${pulse} 2s ease-in-out infinite`,
        }),
        '&::before': animated
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.4),
            transparent
          )`,
              animation: `${shimmer} 2s ease-in-out infinite`,
              zIndex: 1,
            }
          : {},
        '& .MuiSkeleton-root': {
          position: 'relative',
          zIndex: 2,
          '&::after': animated
            ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(
              45deg,
              rgba(255, 255, 255, 0.1),
              rgba(255, 255, 255, 0.3),
              rgba(255, 255, 255, 0.1)
            )`,
                backgroundSize: '200% 200%',
                animation: `${gradientWave} 3s ease-in-out infinite`,
                borderRadius: 'inherit',
              }
            : {},
        },
        ...props.sx,
      }}
      {...props}
    >
      {getCardContent()}
    </Card>
  );
};

SkeletonCard.propTypes = {
  variant: PropTypes.oneOf(['stat', 'action', 'wide', 'indicator-summary']),
  animated: PropTypes.bool,
  className: PropTypes.string,
  sx: PropTypes.object,
};

SkeletonCard.defaultProps = {
  variant: 'stat',
  animated: true,
  className: undefined,
  sx: {},
};

export default SkeletonCard;
